import clsx from 'clsx';
import { ChevronDown, Plus, TriangleAlert } from 'lucide-react';
import { useState } from 'react';
import MutedText from 'components/common/MutedText';
import { HubertButton } from 'components/HubertUI/HubertButton';
import HubertCollapsible from 'components/HubertUI/HubertCollapsible';
import HubertInput from 'components/HubertUI/HubertInput';
import HubertLabel from 'components/HubertUI/HubertLabel';
import HubertSwitch from 'components/HubertUI/HubertSwitch';
import HubertTextArea from 'components/HubertUI/HubertTextArea';
import { useQuestionEditorContext } from 'components/Questionsv2/builder/context/create-question.context';
import { useAppDispatch, useAppSelector } from 'rtk/query/hooks';
import { selectQuestionIsReadonly } from 'rtk/questionEditorSelector';
import { addObjective, updateObjectives } from 'rtk/Question.Editor.Slice';
import FieldLabel from '../SlotFilling/FieldLabel';
import Objective from './Objective';

const GUIDANCE_INFO = (
  <>
    Model-only policy that applies across <strong>all</strong> objectives — never shown to the candidate, write it in
    English. Use it for the role context and the probing style: how specific an answer has to be, what to prefer when
    two objectives are both unresolved, what not to chase.
  </>
);

const FOLLOWUPS_INFO = (
  <>
    How many follow-ups Hubert may ask before accepting whatever it has. One per turn, always aimed at the single most
    valuable gap. The engine caps this at <strong>4</strong> whatever you enter, and stops early anyway if the candidate
    declines, repeats themselves, or covers everything.
  </>
);

const ACTIVE_INFO = (
  <>
    Turn off to park the objectives without deleting them: the question reverts to an ordinary open question and Hubert
    asks no follow-ups. Deleting the last objective has the same effect.
  </>
);

// Objectives: LLM-driven follow-up probing layered on an Open question — the sibling of slot filling.
// HUBERT-only for now (gated by CreateOpen). Slot filling extracts typed fields; this one keeps asking
// until the recruiter's objectives are covered, and the model decides what to ask.
const ObjectivesContainer = () => {
  const dispatch = useAppDispatch();
  const { questionId } = useQuestionEditorContext();
  const objectives = useAppSelector((state) => state.questionEditor.byId[questionId].objectives);
  const slotFilling = useAppSelector((state) => state.questionEditor.byId[questionId].slot_filling);
  const isReadOnly = useAppSelector((state) => selectQuestionIsReadonly(state, questionId));
  const [open, setOpen] = useState(false);

  if (isReadOnly) {
    return null;
  }

  const list = objectives?.list ?? [];
  const isActive = objectives?.active !== false;

  // Ids are the storage keys for coverage verdicts, so empty/duplicate ids silently lose data (two
  // objectives sharing an id collapse into one verdict).
  const ids = list.map((objective) => objective.id.trim());
  const hasEmptyId = ids.some((id) => id.length === 0);
  const duplicateIds = [...new Set(ids.filter((id, i) => id.length > 0 && ids.indexOf(id) !== i))];
  const missingText = list.some((objective) => objective.text.trim().length === 0);
  // Only one rail runs per question, and slot filling wins — say so here rather than let it look broken.
  const shadowedBySlotFilling = list.length > 0 && (slotFilling?.slots.length ?? 0) > 0;

  return (
    <HubertCollapsible className="p-1" open={open} onOpenChange={setOpen}>
      <div className="mb-1 flex flex-row items-center justify-between">
        <HubertCollapsible.Trigger className="flex cursor-pointer flex-row items-center gap-2">
          <ChevronDown
            size={16}
            className={`cursor-pointer transition-transform duration-200 ${open ? 'rotate-180' : 'rotate-0'}`}
          />
          <HubertLabel className="cursor-pointer">
            Objectives
            <MutedText className="cursor-pointer" text=" - let Hubert probe until it has what you asked for" />
          </HubertLabel>
          {list.length > 0 && (
            <span className="rounded-full bg-gray-200 px-2 py-0.5 text-xs font-medium text-gray-600">
              {list.length}
            </span>
          )}
          {list.length > 0 && !isActive && (
            <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-500">off</span>
          )}
        </HubertCollapsible.Trigger>

        {list.length > 0 && (
          <HubertButton size="sm" iconLeft={<Plus size={14} />} onClick={() => dispatch(addObjective({ questionId }))}>
            Add objective
          </HubertButton>
        )}
      </div>

      <HubertCollapsible.Content className="flex flex-col gap-3">
        {list.length === 0 ? (
          <div className={clsx('flex flex-col items-start gap-2', 'rounded-md border border-dashed', 'p-4')}>
            <MutedText
              className="text-xs"
              text="Say what you want to learn, and Hubert works out the follow-ups itself: it judges each objective against the answer and asks about the biggest gap, in the candidate's own language. Use this instead of slot filling when a good answer can't be reduced to fields."
            />
            <HubertButton
              size="sm"
              iconLeft={<Plus size={14} />}
              onClick={() => dispatch(addObjective({ questionId }))}
            >
              Add first objective
            </HubertButton>
          </div>
        ) : (
          // Keyed by position, NOT by objective.id — the id is editable, and keying on it remounts the
          // row on every keystroke, which drops input focus.
          list.map((objective, index) => (
            <Objective key={`objective-${index}`} objective={objective} objectiveIndex={index} />
          ))
        )}

        {(hasEmptyId || duplicateIds.length > 0 || missingText || shadowedBySlotFilling) && (
          <div
            className={clsx(
              'flex flex-row items-start gap-2',
              'border-danger-200 bg-danger-50 rounded-md border',
              'text-danger-700 p-3 text-sm',
            )}
          >
            <TriangleAlert size={16} className="mt-0.5 shrink-0" />
            <div className="flex flex-col gap-0.5">
              {hasEmptyId && <span>Every objective needs an id (e.g. team_size).</span>}
              {duplicateIds.length > 0 && <span>Duplicate ids: {duplicateIds.join(', ')}.</span>}
              {missingText && <span>Every objective needs a description of what Hubert should learn.</span>}
              {shadowedBySlotFilling && (
                <span>
                  This question also has slot filling, which takes precedence — these objectives will be ignored. Use
                  one or the other.
                </span>
              )}
            </div>
          </div>
        )}

        {list.length > 0 && (
          <div className="flex flex-col gap-3 border-t pt-3">
            <div className="flex flex-col gap-1">
              <FieldLabel label="Probing guidance" hint="optional, model-only, English" info={GUIDANCE_INFO} />
              <HubertTextArea
                rows={3}
                placeholder="e.g. First-line manager role. Prefer specifics over adjectives, and accept an answer once it names a number and an outcome."
                value={objectives?.guidance ?? ''}
                onChange={(e) => dispatch(updateObjectives({ questionId, patch: { guidance: e.target.value } }))}
              />
            </div>

            <div className="flex flex-col gap-1">
              <FieldLabel label="Max follow-ups" hint="default 2, max 4" info={FOLLOWUPS_INFO} />
              <HubertInput
                type="number"
                className="w-24"
                value={objectives?.max_followups ?? ''}
                onChange={(e) =>
                  dispatch(
                    updateObjectives({
                      questionId,
                      patch: { max_followups: e.target.value === '' ? undefined : Number(e.target.value) },
                    }),
                  )
                }
              />
            </div>

            <div className="flex flex-row items-center gap-2">
              <HubertSwitch
                checked={isActive}
                onCheckedChange={(value) => dispatch(updateObjectives({ questionId, patch: { active: value } }))}
              />
              <FieldLabel label="Active" info={ACTIVE_INFO} />
            </div>
          </div>
        )}
      </HubertCollapsible.Content>
    </HubertCollapsible>
  );
};

export default ObjectivesContainer;
